import type * as React from "react";
import type { TooltipProps } from "recharts";
import type { Payload as TooltipPayload } from "recharts/types/component/DefaultTooltipContent";
import type { LevelConfig } from "../../modules/emissions/data-config";
import {
    EMISSIONS_LINE_LABELS,
    EMISSIONS_UNIT,
    formatNumberToTwoDecimals,
} from "../../modules/emissions/emissions-chart-utils";
import { AreaSeparator } from "../AreaSeparator";
import { EmissionsChartTooltipBoxes, TooltipCard, TooltipItem } from ".";

type TooltipEntry = TooltipPayload<number, string>;

interface EmissionsChartTooltipProps extends TooltipProps<number, string> {
    payload?: readonly TooltipEntry[];
    levelConfig?: LevelConfig;
}

export const EmissionsChartTooltip: React.FC<EmissionsChartTooltipProps> = ({
    active,
    payload = [],
    levelConfig,
}: EmissionsChartTooltipProps) => {
    if (active && payload.length) {
        const areaKeys = ["area1", "area2", "area3", "area4", "area5"];
        const lineKeys = ["utenEnova", "klimamal"];
        const areaOrderByKey = (levelConfig?.navItems ?? []).reduce<Record<string, number>>((acc, item, idx) => {
            acc[item.area] = idx;
            return acc;
        }, {});

        const areaEntries = payload
            .filter((entry): entry is TooltipEntry => {
                return Boolean(entry && typeof entry.value === "number" && areaKeys.includes(String(entry.dataKey)));
            })
            .sort((a, b) => {
                const aIdx = areaOrderByKey[String(a.dataKey)] ?? Number.MAX_SAFE_INTEGER;
                const bIdx = areaOrderByKey[String(b.dataKey)] ?? Number.MAX_SAFE_INTEGER;
                return aIdx - bIdx;
            });

        const lineEntries = payload.filter((entry): entry is TooltipEntry => {
            return Boolean(entry && typeof entry.value === "number" && lineKeys.includes(String(entry.dataKey)));
        });

        const year = payload[0]?.payload?.year;
        const utenEnovaEntry = lineEntries.find((entry) => entry.dataKey === "utenEnova");
        const klimamalEntry = lineEntries.find((entry) => entry.dataKey === "klimamal");

        const total = areaEntries.reduce((sum, entry) => {
            return sum + (typeof entry.value === "number" ? entry.value : 0);
        }, 0);

        const colorByAreaKey = (levelConfig?.navItems || []).reduce<Record<string, string>>((acc, item) => {
            acc[item.area] = item.fill;
            return acc;
        }, {});

        const utenEnovaValue = typeof utenEnovaEntry?.value === "number" ? utenEnovaEntry.value : undefined;
        const utviklingMedEnovaSuffix = (() => {
            if (utenEnovaValue === undefined) {
                return undefined;
            }
            const difference = utenEnovaValue - total;
            const roundedDifference = Math.round(difference * 100) / 100;
            if (roundedDifference === 0) {
                return undefined;
            }
            const sign = roundedDifference >= 0 ? "-" : "+";
            const absValue = formatNumberToTwoDecimals(Math.abs(roundedDifference));
            return `${sign}${absValue}`;
        })();

        return (
            <TooltipCard title={year}>
                {utenEnovaEntry && (
                    <>
                        <TooltipItem
                            label={String(utenEnovaEntry.name)}
                            value={typeof utenEnovaEntry.value === "number" ? utenEnovaEntry.value : 0}
                            unit={EMISSIONS_UNIT}
                            formatValue={formatNumberToTwoDecimals}
                        />
                        <AreaSeparator />
                    </>
                )}

                <TooltipItem
                    label={EMISSIONS_LINE_LABELS.medEnova}
                    value={total}
                    unit={EMISSIONS_UNIT}
                    formatValue={formatNumberToTwoDecimals}
                    suffix={utviklingMedEnovaSuffix}
                />
                <EmissionsChartTooltipBoxes entries={areaEntries} colorByAreaKey={colorByAreaKey} />

                {klimamalEntry && (
                    <>
                        <AreaSeparator />
                        <TooltipItem
                            label={String(klimamalEntry.name)}
                            value={typeof klimamalEntry.value === "number" ? klimamalEntry.value : 0}
                            unit={EMISSIONS_UNIT}
                            formatValue={formatNumberToTwoDecimals}
                        />
                    </>
                )}
            </TooltipCard>
        );
    }
    return null;
};
